import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, ClipboardCheck, ChevronDown, ChevronUp, Play, RotateCcw,
  FileText, Download, StickyNote, Check, CircleDashed, Circle, CheckCircle2, AlertTriangle,
} from "lucide-react";

// Debounce a callback so rapid ticks are batched into one save.
const useDebouncedSave = (fn, delay = 700) => {
  const timer = useRef();
  return (payload) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => fn(payload), delay);
  };
};

const OUTCOMES = [
  { key: "Passed", label: "Passed", tone: "bg-emerald-500", ring: "ring-emerald-500" },
  { key: "Passed with concerns", label: "Passed w/ concerns", tone: "bg-amber-500", ring: "ring-amber-500" },
  { key: "Failed", label: "Failed / follow-up", tone: "bg-red-500", ring: "ring-red-500" },
];

const JobChecklist = ({ job, onJobUpdated }) => {
  const [state, setState] = useState({ templates: [], suggested_template_id: null, checklist: null });
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [collapsed, setCollapsed] = useState({}); // { [sectionIndex]: boolean }
  const [saveStatus, setSaveStatus] = useState("idle"); // 'idle'|'saving'|'saved'
  const [pickerId, setPickerId] = useState("");
  const [showComplete, setShowComplete] = useState(false);
  const [outcome, setOutcome] = useState("Passed");
  const [technicianNotes, setTechnicianNotes] = useState("");
  const [postTimeline, setPostTimeline] = useState(true);
  const [pendingItems, setPendingItems] = useState([]); // buffered patches waiting to be sent
  const pendingRef = useRef([]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/jobs/${job.id}/checklist`);
      setState(res.data);
      setPickerId(res.data.suggested_template_id || (res.data.templates?.[0]?.id ?? ""));
      setTechnicianNotes(res.data.checklist?.technician_notes || "");
    } catch (err) {
      toast.error("Couldn't load checklist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [job.id]); // eslint-disable-line

  const checklist = state.checklist;
  const sections = checklist?.sections || [];
  const allItems = useMemo(() => sections.flatMap((s) => s.items || []), [sections]);
  const total = allItems.length;
  const done = allItems.filter((i) => i.checked).length;
  const skipped = allItems.filter((i) => i.skipped).length;
  const percent = total ? Math.round((done / total) * 100) : 0;
  const isComplete = !!checklist?.completed_at;

  const startFromTemplate = async () => {
    if (!pickerId) return;
    setStarting(true);
    try {
      const res = await api.post(`/admin/jobs/${job.id}/checklist/start`, { template_id: pickerId });
      onJobUpdated?.(res.data);
      await load();
      toast.success("Checklist started");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start checklist");
    } finally {
      setStarting(false);
    }
  };

  const resetChecklist = async () => {
    if (!window.confirm("Reset this checklist? All ticks and notes will be lost.")) return;
    setResetting(true);
    try {
      const res = await api.post(`/admin/jobs/${job.id}/checklist/reset`);
      onJobUpdated?.(res.data);
      await load();
      toast.success("Checklist reset");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const flushSave = async (extraNotes) => {
    if (!checklist || isComplete) return;
    const patches = pendingRef.current;
    if (patches.length === 0 && extraNotes === undefined) return;
    setSaveStatus("saving");
    try {
      const payload = { items: patches };
      if (extraNotes !== undefined) payload.technician_notes = extraNotes;
      const res = await api.put(`/admin/jobs/${job.id}/checklist`, payload);
      pendingRef.current = [];
      setPendingItems([]);
      setState((s) => ({ ...s, checklist: res.data.checklist }));
      onJobUpdated?.(res.data);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 1200);
    } catch (err) {
      setSaveStatus("idle");
      toast.error("Autosave failed — please retry");
    }
  };

  const debouncedFlush = useDebouncedSave(() => flushSave(), 700);
  const debouncedNotesFlush = useDebouncedSave((n) => flushSave(n), 1000);

  const patchItem = (sectionIdx, key, updates) => {
    // Optimistic local update
    setState((s) => {
      const nx = JSON.parse(JSON.stringify(s));
      const item = nx.checklist.sections[sectionIdx].items.find((i) => i.key === key);
      if (item) {
        if ("checked" in updates) { item.checked = updates.checked; if (item.checked) item.skipped = false; }
        if ("skipped" in updates) { item.skipped = updates.skipped; if (item.skipped) item.checked = false; }
        if ("note" in updates) item.note = updates.note;
      }
      return nx;
    });
    // Merge into pending patches (last-write-wins per key)
    const merged = [...pendingRef.current];
    const existing = merged.findIndex((p) => p.section_index === sectionIdx && p.key === key);
    const next = existing >= 0 ? { ...merged[existing], ...updates } : { section_index: sectionIdx, key, ...updates };
    if (existing >= 0) merged[existing] = next; else merged.push(next);
    pendingRef.current = merged;
    setPendingItems(merged);
    debouncedFlush();
  };

  const openComplete = () => {
    setOutcome("Passed");
    setTechnicianNotes(checklist?.technician_notes || "");
    setPostTimeline(true);
    setShowComplete(true);
  };

  const submitComplete = async () => {
    setCompleting(true);
    try {
      // Flush pending first
      await flushSave(technicianNotes);
      const res = await api.post(`/admin/jobs/${job.id}/checklist/complete`, {
        outcome,
        technician_notes: technicianNotes,
        post_timeline_update: postTimeline,
      });
      setState((s) => ({ ...s, checklist: res.data.checklist, checklist_pdf: res.data.checklist_pdf }));
      onJobUpdated?.(res.data);
      setShowComplete(false);
      toast.success("Checklist completed · PDF saved to Documents");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Complete failed");
    } finally {
      setCompleting(false);
    }
  };

  const openPdf = async () => {
    const win = window.open("", "_blank");
    try {
      const res = await api.get(`/admin/jobs/${job.id}/checklist/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (win && !win.closed) win.location.href = url;
      else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      if (win && !win.closed) win.close();
      toast.error("Open failed");
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/admin/jobs/${job.id}/checklist/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Spark-Checklist-${job.ref_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 1000);
    } catch {
      toast.error("Download failed");
    }
  };

  if (loading) {
    return (
      <div className="surface-card p-6 sm:p-8 flex items-center gap-3 text-spark-slate">
        <Loader2 className="size-4 animate-spin" /> Loading checklist…
      </div>
    );
  }

  // No checklist yet — show starter
  if (!checklist) {
    return (
      <div className="surface-card p-6 sm:p-8" data-testid="checklist-starter">
        <div className="flex items-center gap-2 mb-1">
          <ClipboardCheck className="size-4 text-spark-teal" />
          <p className="label-mono text-spark-teal">Service Checklist</p>
        </div>
        <p className="text-xs text-spark-slate mb-4">
          Pick a template to walk through this repair item-by-item. Progress auto-saves. Complete the checklist to generate a PDF summary attached to Documents and post a customer-visible note.
        </p>
        {state.suggested_template_id && (
          <div className="rounded-md bg-spark-yellow/20 border border-spark-yellow px-3 py-2 text-xs text-spark-ink mb-3 flex items-center gap-2" data-testid="checklist-suggested-banner">
            <AlertTriangle className="size-3.5 text-amber-700" />
            Suggested from this job&apos;s services: <strong className="font-display uppercase tracking-tight">
              {state.templates.find((t) => t.id === state.suggested_template_id)?.name}
            </strong>
          </div>
        )}
        <div className="flex flex-wrap gap-2 items-center">
          <select
            className="input-style flex-1 min-w-[220px]"
            value={pickerId}
            onChange={(e) => setPickerId(e.target.value)}
            data-testid="checklist-template-picker"
          >
            {state.templates.map((t) => {
              const totalItems = (t.sections || []).reduce((n, s) => n + (s.items?.length || 0), 0);
              return <option key={t.id} value={t.id}>{t.name} · {totalItems} items</option>;
            })}
          </select>
          <button
            onClick={startFromTemplate}
            disabled={!pickerId || starting}
            className="btn-primary text-xs"
            data-testid="checklist-start-btn"
          >
            {starting ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />} Start checklist
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="surface-card p-6 sm:p-8" data-testid="checklist-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <ClipboardCheck className="size-4 text-spark-teal" />
            <p className="label-mono text-spark-teal">Service Checklist</p>
            {isComplete && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] uppercase tracking-wider font-bold">
                <CheckCircle2 className="size-3" /> {checklist.outcome || "Completed"}
              </span>
            )}
            {!isComplete && saveStatus === "saving" && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-spark-slate"><Loader2 className="size-3 animate-spin" /> Saving…</span>
            )}
            {!isComplete && saveStatus === "saved" && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-600"><Check className="size-3" /> Saved</span>
            )}
          </div>
          <p className="text-sm font-display uppercase tracking-tight font-bold text-spark-ink truncate">{checklist.template_name}</p>
        </div>
        <div className="flex gap-1.5 shrink-0">
          {isComplete && (
            <>
              <button onClick={openPdf} className="btn-outline-ink !py-1.5 !px-3 text-xs" data-testid="checklist-open-pdf"><FileText className="size-3.5" /> Open PDF</button>
              <button onClick={downloadPdf} className="btn-outline-ink !py-1.5 !px-3 text-xs" data-testid="checklist-download-pdf"><Download className="size-3.5" /></button>
            </>
          )}
          <button
            onClick={resetChecklist}
            disabled={resetting}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider border border-spark-line text-spark-slate hover:border-red-500 hover:text-red-500"
            title="Reset checklist"
            data-testid="checklist-reset-btn"
          >
            {resetting ? <Loader2 className="size-3.5 animate-spin" /> : <RotateCcw className="size-3.5" />}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-4" data-testid="checklist-progress">
        <div className="flex items-center justify-between text-xs text-spark-slate mb-1.5">
          <span><strong className="text-spark-ink">{done}</strong> / {total} done{skipped ? ` · ${skipped} skipped` : ""}</span>
          <span className="font-mono">{percent}%</span>
        </div>
        <div className="h-2 rounded-full bg-spark-line overflow-hidden">
          <div
            className={`h-full transition-all ${isComplete ? "bg-emerald-500" : "bg-spark-teal"}`}
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-2">
        {sections.map((sec, si) => {
          const secDone = (sec.items || []).filter((i) => i.checked).length;
          const secTotal = (sec.items || []).length;
          const isCollapsed = collapsed[si];
          return (
            <div key={si} className="rounded-md border border-spark-line overflow-hidden" data-testid={`checklist-section-${si}`}>
              <button
                type="button"
                onClick={() => setCollapsed((s) => ({ ...s, [si]: !s[si] }))}
                className="w-full flex items-center justify-between gap-3 px-3 py-2.5 bg-spark-floor hover:bg-white transition"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {isCollapsed ? <ChevronDown className="size-4 text-spark-slate shrink-0" /> : <ChevronUp className="size-4 text-spark-slate shrink-0" />}
                  <span className="font-display uppercase tracking-tight font-bold text-spark-ink text-sm truncate">{sec.title}</span>
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${secDone === secTotal && secTotal > 0 ? "bg-emerald-100 text-emerald-800" : "bg-white text-spark-slate border border-spark-line"}`}>
                  {secDone}/{secTotal}
                </span>
              </button>
              {!isCollapsed && (
                <ul className="divide-y divide-spark-line">
                  {(sec.items || []).map((it) => (
                    <ChecklistRow
                      key={it.key}
                      item={it}
                      readOnly={isComplete}
                      onToggle={() => patchItem(si, it.key, { checked: !it.checked })}
                      onSkip={() => patchItem(si, it.key, { skipped: !it.skipped })}
                      onNote={(note) => patchItem(si, it.key, { note })}
                    />
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {/* Technician notes */}
      <div className="mt-5 pt-5 border-t border-spark-line">
        <label className="label-mono mb-2 inline-flex items-center gap-1.5"><StickyNote className="size-3" /> Technician notes</label>
        <textarea
          rows={3}
          className="input-style resize-none"
          placeholder="Anything the customer should know, spare parts needed, follow-ups…"
          value={technicianNotes}
          onChange={(e) => {
            setTechnicianNotes(e.target.value);
            if (!isComplete) debouncedNotesFlush(e.target.value);
          }}
          disabled={isComplete}
          data-testid="checklist-technician-notes"
        />
      </div>

      {!isComplete && (
        <div className="mt-4 flex items-center justify-end gap-2 flex-wrap">
          <span className="text-xs text-spark-slate mr-auto">Progress auto-saves as you tick items.</span>
          <button onClick={openComplete} className="btn-accent text-xs" data-testid="checklist-complete-btn">
            <CheckCircle2 className="size-4" /> Complete checklist
          </button>
        </div>
      )}
    </div>

    {/* Complete modal */}
    {showComplete && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="checklist-complete-modal" role="dialog" aria-modal="true">
        <div className="w-full max-w-md rounded-xl border border-spark-line bg-white shadow-2xl overflow-hidden">
          <div className="bg-spark-teal-deep px-6 py-4">
            <p className="label-mono text-spark-yellow">Complete checklist</p>
            <h2 className="font-display uppercase tracking-tight text-lg font-bold text-white">Sign off this service</h2>
          </div>
          <div className="px-6 py-5 space-y-4 text-sm text-spark-ink">
            <div>
              <label className="label-mono mb-2 inline-block">Outcome</label>
              <div className="grid grid-cols-1 gap-1.5">
                {OUTCOMES.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => setOutcome(o.key)}
                    className={`text-left flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition ${outcome === o.key ? "border-spark-ink bg-spark-floor ring-2 " + o.ring : "border-spark-line hover:border-spark-ink"}`}
                    data-testid={`checklist-outcome-${o.key.replace(/\s+/g,'-').toLowerCase()}`}
                  >
                    <span className={`inline-block size-3 rounded-full ${o.tone}`} />
                    <span className="font-display uppercase tracking-tight font-bold">{o.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="label-mono mb-2 inline-block">Final technician notes (optional)</label>
              <textarea
                rows={3}
                className="input-style resize-none"
                placeholder="Overall condition, next-service recommendations…"
                value={technicianNotes}
                onChange={(e) => setTechnicianNotes(e.target.value)}
                data-testid="checklist-complete-notes"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-xs text-spark-slate">
              <input type="checkbox" checked={postTimeline} onChange={(e) => setPostTimeline(e.target.checked)} className="size-4 accent-spark-teal" data-testid="checklist-post-timeline-toggle" />
              Also post a customer-visible update to the tracker
            </label>
            <div className="rounded-md bg-spark-floor border border-spark-line p-3 text-xs text-spark-slate">
              <p><strong className="text-spark-ink">{done}</strong> ticked · <strong className="text-spark-ink">{skipped}</strong> skipped · <strong className="text-spark-ink">{total - done - skipped}</strong> pending.</p>
              <p className="mt-1">A branded PDF summary will be generated and saved to this job&apos;s Documents.</p>
            </div>
          </div>
          <div className="px-6 py-4 bg-spark-floor border-t border-spark-line flex items-center justify-end gap-2">
            <button onClick={() => setShowComplete(false)} disabled={completing} className="px-4 py-2 rounded-md text-xs uppercase tracking-wider font-semibold text-spark-slate hover:bg-white" data-testid="checklist-complete-cancel">Cancel</button>
            <button onClick={submitComplete} disabled={completing} className="btn-accent text-xs" data-testid="checklist-complete-confirm">
              {completing ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />} Sign off
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

const ChecklistRow = ({ item, readOnly, onToggle, onSkip, onNote }) => {
  const [noteOpen, setNoteOpen] = useState(!!item.note);
  const [localNote, setLocalNote] = useState(item.note || "");
  useEffect(() => { setLocalNote(item.note || ""); }, [item.note]);

  return (
    <li className="px-3 py-2.5 bg-white" data-testid={`checklist-item-${item.key}`}>
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={onToggle}
          disabled={readOnly}
          className={`shrink-0 mt-0.5 size-5 rounded-md border flex items-center justify-center transition ${item.checked ? "bg-emerald-500 border-emerald-500 text-white" : "border-spark-line hover:border-spark-teal"} ${readOnly ? "opacity-70 cursor-not-allowed" : ""}`}
          aria-label="Toggle done"
          data-testid={`checklist-toggle-${item.key}`}
        >
          {item.checked && <Check className="size-3" strokeWidth={3} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm ${item.checked ? "text-spark-slate line-through" : "text-spark-ink"}`}>{item.label}</span>
            {item.skipped && <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">N/A</span>}
          </div>
          {noteOpen && (
            <input
              type="text"
              value={localNote}
              onChange={(e) => {
                setLocalNote(e.target.value);
                onNote(e.target.value);
              }}
              disabled={readOnly}
              placeholder="Add a note (e.g. pressure 35 psi, pads at 60%)"
              className="mt-1.5 w-full text-xs border border-spark-line rounded-sm px-2 py-1.5 focus:outline-none focus:border-spark-teal"
              data-testid={`checklist-note-${item.key}`}
            />
          )}
        </div>
        <div className="shrink-0 flex items-center gap-1">
          {!readOnly && (
            <button
              type="button"
              onClick={() => setNoteOpen((s) => !s)}
              className={`p-1 rounded ${item.note || noteOpen ? "text-spark-teal" : "text-spark-slate hover:text-spark-ink"}`}
              title={noteOpen ? "Hide note" : "Add note"}
              data-testid={`checklist-note-toggle-${item.key}`}
            >
              <StickyNote className="size-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={onSkip}
            disabled={readOnly}
            className={`p-1 rounded ${item.skipped ? "text-amber-700" : "text-spark-slate hover:text-spark-ink"}`}
            title="Mark N/A (skip)"
            data-testid={`checklist-skip-${item.key}`}
          >
            {item.skipped ? <CircleDashed className="size-3.5" /> : <Circle className="size-3.5" />}
          </button>
        </div>
      </div>
    </li>
  );
};

export default JobChecklist;
